import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, ValidationErrors } from '@angular/forms';

/** Campos de fecha del traslado que tienen su propio calendario. */
type TransferDateField = 'pickupDate' | 'returnDate';

/** Tramo del traslado al que pertenecen las preferencias. */
export type PreferenceLeg = 'ida' | 'vuelta';

/**
 * Vuelo en el modelo del Lovable: una entrada por tramo (llegada / salida).
 *
 * Los vuelos del file (endpoint `itineraries/{id}`) vienen con los dos
 * tramos dentro del mismo registro (`arrivalIn` / `departureOut`): se
 * convierten a dos entradas, una por tramo.
 */
export interface FlightLeg {
  id: string;
  source: 'file' | 'manual';
  direction: string;
  airline: string;
  number: string;
  airport: string;
  date: string;
  time: string;
}

/** Vuelo cargado a mano por el agente. */
interface ManualFlightDraft {
  direction: string;
  airline: string;
  number: string;
  airport: string;
  date: string;
  time: string;
}

/**
 * Estado de UI de cada traslado (se mantiene en paralelo al FormArray,
 * indexado por la posición del traslado). No viaja en el payload.
 */
export interface TransferCardState {
  /** ¿La card está desplegada? */
  open: boolean;
  /** ¿El usuario apretó "Guardar" en esta card? */
  saved: boolean;
  /** Tramo visible en las tabs de preferencias. */
  activeLeg: PreferenceLeg;
  /** ¿El usuario editó las preferencias de la vuelta a mano? */
  returnLegTouched: boolean;
}

const MONTH_LABELS = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const DAY_OF_WEEK_LABELS = ['LU', 'MA', 'MI', 'JU', 'VI', 'SÁ', 'DO'];

@Component({
  selector: 'usb-transfer-form',
  templateUrl: './transfer-form.component.html',
  styleUrls: ['./transfer-form.component.scss']
})
export class UsbTransferFormComponent implements OnInit {
  @Input() formGroup: FormGroup;
  /**
   * Factory de grupos de traslado. La provee el componente padre
   * (`FormOfflineComponent`) para que los grupos se creen con sus validators
   * (rango de fechas, edades de niños) y no se dupliquen acá.
   */
  @Input() createTransferGroup: () => FormGroup;
  @Input() selectedFile: any;
  @Input() selectedClient: any;
  @Input() availablePax: any[];
  @Input() loadingFiles: boolean;
  @Input() loadingPax: boolean;
  @Input() loadingCountries: boolean;
  @Input() showNewFileNameInput: boolean;
  @Input() showNewClientInput: boolean;
  @Input() existingFiles: any[];
  @Input() countriesData: any[];
  @Input() newFilePassenger: string;
  @Input() newFilePassengerError: boolean;
  @Input() newFilePhoneCode: string;
  @Input() newFilePhoneCodeError: boolean;
  @Input() newFilePhone: string;
  @Input() newFilePhoneError: boolean;
  @Input() newFileEmail: string;
  @Input() newFileEmailError: boolean;
  @Input() phoneCodeSelected: string;
  @Input() selectedFileId: string | null;
  @Input() selectedClientId: string | null;
  @Input() edit: boolean = false;
  @Input() requiredFieldMessage: (tab: string, field: string) => string;
  @Input() getFileLabel: (f: any) => string;
  @Input() getClientLabel: (c: any) => string;
  @Input() getClientsForSelectedFile: () => any[];
  @Input() isNuevoFileValid: () => boolean;
  @Input() blockInvalidKeys: (event: KeyboardEvent, type: string) => void;
  @Input() requestReady: boolean;
  @Input() showClearDialog: boolean = false;
  /** Vuelos del file seleccionado (bloque "Vuelos asociados"). */
  @Input()
  set fileFlights(value: any[]) {
    this._fileFlights = value ?? [];
    this.availableFlightsCache = null;
  }
  get fileFlights(): any[] {
    return this._fileFlights;
  }
  private _fileFlights: any[] = [];

  @Input() loadingFlights: boolean = false;

  @Output() submit = new EventEmitter<any>();
  @Output() clear = new EventEmitter<void>();
  @Output() clearDialogChange = new EventEmitter<boolean>();
  @Output() fileChange = new EventEmitter<any>();
  @Output() clientChange = new EventEmitter<any>();
  @Output() paxChange = new EventEmitter<any>();
  @Output() requestTypeChange = new EventEmitter<any>();
  @Output() newFileToggle = new EventEmitter<void>();
  @Output() newClientToggle = new EventEmitter<void>();
  @Output() newFileSubmit = new EventEmitter<void>();
  @Output() newClientSubmit = new EventEmitter<any>();
  @Output() fileSearchInput = new EventEmitter<string>();
  @Output() clearSelectedFile = new EventEmitter<void>();
  @Output() passengerIdChange = new EventEmitter<any>();
  @Output() passengerFirstNameChange = new EventEmitter<string>();
  @Output() passengerLastNameChange = new EventEmitter<string>();
  @Output() newFilePassengerInput = new EventEmitter<string>();
  @Output() newFilePhoneCodeInput = new EventEmitter<string>();
  @Output() newFileCountrySelected = new EventEmitter<any>();
  @Output() newFilePhoneInput = new EventEmitter<string>();
  @Output() newFileEmailInput = new EventEmitter<string>();
  @Output() countrySelected = new EventEmitter<any>();
  @Output() phoneCodeInput = new EventEmitter<string>();
  @Output() clientFirstNameChange = new EventEmitter<string>();
  @Output() clientLastNameChange = new EventEmitter<string>();
  @Output() clientEmailChange = new EventEmitter<string>();
  @Output() clientPhoneChange = new EventEmitter<string>();

  readonly modalityOptions = ['Privado', 'Regular'];
  readonly categoryOptions = ['Estándar', 'Lujo'];
  readonly ageOptions = Array.from({ length: 18 }, (_, i) => i);

  readonly vehicleOptions = [
    'Sedán', 'Van', 'Minivan', 'Camioneta', 'Minibús',
    'Autobús', 'Vehículo de lujo', 'Limusina', 'Pick-up', 'Blindado'
  ];

  readonly monthLabels = MONTH_LABELS;
  readonly dayOfWeekLabels = DAY_OF_WEEK_LABELS;

  /** Tope de pasajeros por tipo (adultos / niños), igual que el hotel. */
  readonly maxPassengers = 10;

  /** Tope de vehículos por traslado (Lovable: "Hasta 10 vehículos"). */
  readonly maxVehicles = 10;

  /**
   * Estado de UI de cada traslado, alineado por índice con el FormArray
   * `transfers`.
   */
  cardStates: TransferCardState[] = [];

  /** Calendario abierto: traslado + campo de fecha que lo está mostrando. */
  openCalendar: { index: number; field: TransferDateField } | null = null;

  /** Mes que se está mostrando en el calendario. */
  calendarCursor: Date = new Date();

  constructor(private fb: FormBuilder) {}

  ngOnInit(): void {
    // Los enlaces de referencia se validan como URL: si no es válida, el
    // formulario queda inválido y el botón "Enviar" se deshabilita.
    this.applyUrlValidator(this.competitorUrlsArray);

    this.syncCardStates();

    // Cada traslado tiene su propia sincronización de preferencias: mientras
    // el usuario no toque la vuelta, la vuelta sigue a la ida.
    this.transfersArray.controls.forEach((group: AbstractControl) => {
      this.bindTransfer(group as FormGroup);
    });
  }

  /**
   * Suscribe los comportamientos propios de un traslado:
   * - la vuelta sigue a la ida mientras el usuario no la edite a mano;
   * - si se borra la fecha de vuelta, la tab activa vuelve a "Ida".
   *
   * La suscripción se ata al GRUPO (y no al índice) para que siga siendo
   * correcta después de agregar o eliminar traslados.
   */
  private bindTransfer(group: FormGroup): void {
    group.get('preferences')?.get('ida')?.valueChanges.subscribe(() => {
      const index = this.transfersArray.controls.indexOf(group);
      if (index >= 0) {
        this.syncIdaPreferencesToReturn(index);
      }
    });

    group.get('returnDate')?.valueChanges.subscribe((value: any) => {
      const index = this.transfersArray.controls.indexOf(group);
      if (index >= 0 && !value) {
        this.setActiveLeg(index, 'ida');
        this.cardStates[index].returnLegTouched = false;
      }
    });
  }

  // ==========================================================
  // Listado de traslados
  // ==========================================================

  /** FormArray con un grupo por traslado. */
  get transfersArray(): FormArray {
    return (this.formGroup?.get('transfers') ?? this.fb.array([])) as FormArray;
  }

  /** Grupo del traslado en la posición indicada. */
  transferGroup(index: number): FormGroup | null {
    return (this.transfersArray?.at(index) as FormGroup) ?? null;
  }

  /** Agrega un traslado vacío (y lo abre). */
  addTransfer(): void {
    const group = this.createTransferGroup?.();
    if (!group) {
      return;
    }
    if (this.edit) {
      group.disable();
    }
    this.transfersArray.push(group);
    this.bindTransfer(group);
    this.syncCardStates();
    const index = this.transfersArray.length - 1;
    this.cardStates[index] = { open: true, saved: false, activeLeg: 'ida', returnLegTouched: false };
  }

  /** Duplica un traslado (copia todos sus valores) y abre el nuevo. */
  duplicateTransfer(index: number): void {
    const source = this.transferGroup(index);
    if (!source) {
      return;
    }
    const group = this.createTransferGroup?.();
    if (!group) {
      return;
    }
    group.patchValue(source.getRawValue());
    // Las edades de los niños se copian como valores (no como controles).
    const childAges = group.get('childAges') as FormArray;
    childAges.clear();
    const sourceAges = source.get('childAges') as FormArray;
    (sourceAges?.value ?? []).forEach((age: any) => {
      childAges.push(this.fb.control(age ?? ''));
    });

    if (this.edit) {
      group.disable();
    }

    this.transfersArray.push(group);
    this.bindTransfer(group);
    this.syncCardStates();
    const newIndex = this.transfersArray.length - 1;
    this.cardStates[newIndex] = { open: true, saved: false, activeLeg: 'ida', returnLegTouched: false };
  }

  /** Elimina un traslado. */
  removeTransfer(index: number): void {
    if (index < 0 || index >= this.transfersArray.length) {
      return;
    }
    this.transfersArray.removeAt(index);
    // El estado de UI se elimina en la misma posición para que los traslados
    // siguientes no cambien de "tarjeta" (abierta / guardada / tramo activo).
    this.cardStates.splice(index, 1);
    if (this.openCalendar?.index === index) {
      this.closeCalendar();
    }
  }

  /** Despliega / repliega la card de un traslado. */
  toggleTransfer(index: number): void {
    const state = this.cardStates[index];
    if (!state) {
      return;
    }
    state.open = !state.open;
    if (!state.open) {
      this.closeCalendar();
    }
  }

  /** Marca el traslado como "guardado" (chip de la card). */
  saveTransfer(index: number): void {
    const group = this.transferGroup(index);
    if (!group) {
      return;
    }
    group.markAllAsTouched();
    group.updateValueAndValidity();
    if (group.invalid) {
      // Si le falta algo, se abre la card para que se vea el error.
      this.cardStates[index].open = true;
      return;
    }
    this.cardStates[index].saved = true;
  }

  /** ¿La card del traslado está desplegada? */
  isOpen(index: number): boolean {
    return this.cardStates[index]?.open ?? true;
  }

  /** ¿El traslado fue marcado como guardado? */
  isSaved(index: number): boolean {
    return this.cardStates[index]?.saved ?? false;
  }

  /** Mantiene el array de estados alineado con el FormArray. */
  private syncCardStates(): void {
    const length = this.transfersArray.length;
    while (this.cardStates.length < length) {
      this.cardStates.push({ open: true, saved: false, activeLeg: 'ida', returnLegTouched: false });
    }
  }

  // ==========================================================
  // Vuelos asociados
  // ==========================================================

  /**
   * Vuelos del file convertidos al modelo del Lovable: cada registro del
   * file tiene un tramo de llegada y uno de salida, y se transforma en una
   * entrada por tramo.
   */
  get fileFlightLegs(): FlightLeg[] {
    const legs: FlightLeg[] = [];
    (this.fileFlights ?? []).forEach((flight: any, index: number) => {
      if (!flight) return;
      const id = flight.id ?? `file-${index}`;
      if (flight.arrivalIn || flight.airlineIn) {
        legs.push({
          id: `${id}-in`,
          source: 'file',
          direction: 'Llegada',
          airline: flight.airlineIn ?? '',
          number: flight.flyNumberIn ?? '',
          airport: flight.airportIn ?? flight.airportIataCodeIn ?? '',
          date: this.datePart(flight.arrivalIn),
          time: this.timePart(flight.arrivalIn)
        });
      }
      if (flight.departureOut || flight.airlineOut) {
        legs.push({
          id: `${id}-out`,
          source: 'file',
          direction: 'Salida',
          airline: flight.airlineOut ?? '',
          number: flight.flyNumberOut ?? '',
          airport: flight.airportOut ?? flight.airportIataCodeOut ?? '',
          date: this.datePart(flight.departureOut),
          time: this.timePart(flight.departureOut)
        });
      }
    });
    return legs;
  }

  /** Vuelos cargados a mano por el agente. */
  get manualFlights(): FlightLeg[] {
    return ((this.formGroup?.get('manualFlights')?.value ?? []) as any[]).map((flight: any) => ({
      id: String(flight?.id ?? ''),
      source: 'manual' as const,
      direction: flight?.direction ?? 'Llegada',
      airline: flight?.airline ?? '',
      number: flight?.number ?? '',
      airport: flight?.airport ?? '',
      date: flight?.date ?? '',
      time: flight?.time ?? ''
    }));
  }

  /** Todos los vuelos disponibles: los del file + los cargados a mano. */
  get availableFlights(): FlightLeg[] {
    if (!this.availableFlightsCache) {
      this.availableFlightsCache = [...this.fileFlightLegs, ...this.manualFlights];
    }
    return this.availableFlightsCache;
  }

  /**
   * Cache de la lista de vuelos: se recalcula sólo cuando cambian los vuelos
   * del file o los cargados a mano. Sin esto, el `*ngFor` se re-renderiza en
   * cada change detection.
   */
  private availableFlightsCache: FlightLeg[] | null = null;

  /** `trackBy` del `*ngFor` de vuelos (evita recrear el DOM en cada ciclo). */
  trackByFlight(index: number, flight: FlightLeg): string {
    return flight.id;
  }

  /** Ids de los vuelos seleccionados en un traslado. */
  selectedFlightIds(index: number): string[] {
    return ((this.transferGroup(index)?.get('flightIds')?.value ?? []) as any[]).map((id: any) => String(id));
  }

  /** Vuelos seleccionados en un traslado (objetos completos). */
  selectedFlights(index: number): FlightLeg[] {
    return (this.transferGroup(index)?.get('flights')?.value ?? []) as FlightLeg[];
  }

  /** ¿El vuelo está asociado a este traslado? */
  isFlightSelected(index: number, flightId: string): boolean {
    return this.selectedFlightIds(index).includes(String(flightId));
  }

  /** Asocia / desasocia un vuelo del traslado. */
  toggleFlight(index: number, flight: FlightLeg): void {
    const group = this.transferGroup(index);
    if (!group) return;

    const flightIds = group.get('flightIds') as FormArray;
    const flights = group.get('flights') as FormArray;
    const id = String(flight.id);
    const position = flightIds.value.map((value: any) => String(value)).indexOf(id);

    if (position >= 0) {
      flightIds.removeAt(position);
      const flightPosition = flights.value.findIndex((value: any) => String(value?.id) === id);
      if (flightPosition >= 0) {
        flights.removeAt(flightPosition);
      }
      return;
    }

    flightIds.push(this.fb.control(id));
    flights.push(this.fb.control(flight));
  }

  // ==========================================================
  // Carga manual de vuelos
  // ==========================================================

  /** ¿Está abierto el formulario de carga manual de vuelos? */
  manualFlightOpen: boolean = false;

  /** Datos del vuelo que se está cargando a mano. */
  manualFlightDraft: ManualFlightDraft = this.emptyManualFlight();

  /** Opciones del selector "Tipo de vuelo". */
  readonly flightDirections = ['Llegada', 'Salida'];

  private emptyManualFlight(): ManualFlightDraft {
    return { direction: 'Llegada', airline: '', number: '', airport: '', date: '', time: '' };
  }

  /** ¿El vuelo en carga tiene todos los datos obligatorios? */
  manualFlightValid(): boolean {
    const draft = this.manualFlightDraft;
    return Boolean(
      (draft.airline || '').trim() &&
      (draft.number || '').trim() &&
      (draft.airport || '').trim() &&
      (draft.date || '').trim() &&
      (draft.time || '').trim()
    );
  }

  /** Abre / cierra el formulario de carga manual. */
  toggleManualFlightForm(): void {
    this.manualFlightOpen = !this.manualFlightOpen;
    if (!this.manualFlightOpen) {
      this.manualFlightDraft = this.emptyManualFlight();
    }
  }

  /** Agrega el vuelo en carga a la lista de vuelos manuales. */
  addManualFlight(): void {
    if (!this.manualFlightValid()) {
      return;
    }
    const array = this.formGroup.get('manualFlights') as FormArray;
    if (!array) {
      return;
    }
    const flight = {
      id: `manual-${Date.now()}`,
      direction: this.manualFlightDraft.direction,
      airline: this.manualFlightDraft.airline.trim(),
      number: this.manualFlightDraft.number.trim(),
      airport: this.manualFlightDraft.airport.trim(),
      date: this.manualFlightDraft.date,
      time: this.manualFlightDraft.time
    };
    array.push(this.fb.control(flight));
    this.availableFlightsCache = null;
    this.manualFlightDraft = this.emptyManualFlight();
    this.manualFlightOpen = false;
  }

  /** Elimina un vuelo manual. */
  removeManualFlight(flightId: string): void {
    const array = this.formGroup.get('manualFlights') as FormArray;
    if (!array) {
      return;
    }
    const position = array.value.findIndex((value: any) => String(value?.id) === String(flightId));
    if (position >= 0) {
      array.removeAt(position);
      this.availableFlightsCache = null;
    }
  }

  /** Fecha (YYYY-MM-DD) de un datetime ISO. */
  private datePart(value: any): string {
    if (!value) return '';
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));
    return match ? `${match[1]}-${match[2]}-${match[3]}` : '';
  }

  /** Hora (HH:mm) de un datetime ISO. */
  private timePart(value: any): string {
    if (!value) return '';
    const match = /T(\d{2}):(\d{2})/.exec(String(value));
    return match ? `${match[1]}:${match[2]}` : '';
  }

  // ==========================================================
  // Datos del traslado
  // ==========================================================

  get vehicleOptionsFirstHalf(): string[] {
    const half = Math.ceil(this.vehicleOptions.length / 2);
    return this.vehicleOptions.slice(0, half);
  }

  get vehicleOptionsSecondHalf(): string[] {
    const half = Math.ceil(this.vehicleOptions.length / 2);
    return this.vehicleOptions.slice(half);
  }

  /** Array de edades de los niños del traslado. */
  childAgesArray(index: number): FormArray {
    const array = this.transferGroup(index)?.get('childAges') as FormArray;
    if (array) {
      return array;
    }
    // Array vacío ESTABLE: si se devolviera uno nuevo en cada change
    // detection, el *ngFor de las edades se re-renderizaría en cada ciclo.
    if (!this.emptyFormArray) {
      this.emptyFormArray = this.fb.array([]);
    }
    return this.emptyFormArray;
  }

  /** Array vacío reutilizable (ver `childAgesArray`). */
  private emptyFormArray: FormArray | null = null;

  /** Enlaces de referencia del bloque de competencia. */
  get competitorUrlsArray(): FormArray {
    return (this.formGroup.get('competitorUrls') ?? this.fb.array([])) as FormArray;
  }

  /** Capturas adjuntas del bloque de competencia (base64 o url pública). */
  get competitorImagesArray(): FormArray {
    return (this.formGroup.get('competitorImages') ?? this.fb.array([])) as FormArray;
  }

  /** Cantidad de niños del traslado. */
  childrenCount(index: number): number {
    return Math.max(0, Math.floor(Number(this.transferGroup(index)?.get('children')?.value) || 0));
  }

  /** Cantidad de adultos del traslado (mínimo 1). */
  adultsCount(index: number): number {
    return Math.max(0, Math.floor(Number(this.transferGroup(index)?.get('adults')?.value) || 0));
  }

  /** Total de pasajeros del traslado. */
  totalPassengers(index: number): number {
    return this.adultsCount(index) + this.childrenCount(index);
  }

  /** true si el control indicado del traslado está inválido y fue tocado. */
  isInvalid(index: number, path: string): boolean {
    const control = this.transferGroup(index)?.get(path);
    return !!control && control.invalid && control.touched;
  }

  /** true si al traslado le faltan las edades de los niños. */
  childAgesInvalid(index: number): boolean {
    const array = this.childAgesArray(index);
    if (!array || array.length === 0) return false;
    return array.invalid && array.touched;
  }

  /** Resumen de vehículos del traslado (para la card plegada). */
  vehicleSummary(index: number): string {
    const vehicles = (this.preferenceGroup(index, 'ida')?.get('vehicles')?.value || []) as string[];
    if (!vehicles.length) {
      return 'Sin vehículo';
    }
    return vehicles.join(', ');
  }

  /** Suma o resta adultos (mínimo 1, máximo `maxPassengers`). */
  changeAdults(index: number, delta: number): void {
    const control = this.transferGroup(index)?.get('adults');
    if (!control) {
      return;
    }
    const next = Math.min(this.maxPassengers, Math.max(1, this.adultsCount(index) + delta));
    control.setValue(String(next));
    control.markAsTouched();
  }

  /** Suma o resta niños (mínimo 0, máximo `maxPassengers`). El array de
   * edades se sincroniza solo (el padre lo hace al cambiar `children`). */
  changeChildren(index: number, delta: number): void {
    const control = this.transferGroup(index)?.get('children');
    if (!control) {
      return;
    }
    const next = Math.min(this.maxPassengers, Math.max(0, this.childrenCount(index) + delta));
    control.setValue(String(next));
    control.markAsTouched();
  }

  /** Cantidad de vehículos del traslado. */
  vehicleCount(index: number): number {
    return Math.max(1, Math.floor(Number(this.transferGroup(index)?.get('vehicleCount')?.value) || 1));
  }

  /** Suma o resta vehículos (mínimo 1, máximo `maxVehicles`). */
  changeVehicleCount(index: number, delta: number): void {
    const control = this.transferGroup(index)?.get('vehicleCount');
    if (!control) {
      return;
    }
    const next = Math.min(this.maxVehicles, Math.max(1, this.vehicleCount(index) + delta));
    control.setValue(String(next));
    control.markAsTouched();
  }

  /** Guarda la edad del niño `childIndex` del traslado `index`. */
  onChildAgeChange(index: number, childIndex: number, age: string): void {
    const array = this.childAgesArray(index);
    const control = array.at(childIndex);
    if (!control) {
      return;
    }
    control.setValue(age);
    control.markAsTouched();
    array.markAsTouched();
  }

  // ==========================================================
  // Preferencias por tramo (ida / vuelta) — por traslado
  // ==========================================================

  /**
   * Grupo de preferencias del tramo indicado de un traslado. Las
   * preferencias viven en `transfers[N].preferences.ida|vuelta`; la "vuelta"
   * sólo aplica cuando el traslado es ida y vuelta (hay fecha de vuelta).
   */
  preferenceGroup(index: number, leg: PreferenceLeg): FormGroup | null {
    const preferences = this.transferGroup(index)?.get('preferences') as FormGroup;
    return (preferences?.get(leg) as FormGroup) ?? null;
  }

  /** true si el traslado tiene vuelta (ida y vuelta). */
  hasReturnLeg(index: number): boolean {
    return !!this.transferGroup(index)?.get('returnDate')?.value;
  }

  /** Tramos visibles en las tabs de preferencias. */
  preferenceLegs(index: number): PreferenceLeg[] {
    return this.hasReturnLeg(index) ? ['ida', 'vuelta'] : ['ida'];
  }

  /** Etiqueta de la tab del tramo. */
  preferenceLegLabel(leg: PreferenceLeg): string {
    return leg === 'ida' ? 'Ida' : 'Vuelta';
  }

  /** Tramo activo de un traslado. */
  activeLegOf(index: number): PreferenceLeg {
    return this.cardStates[index]?.activeLeg ?? 'ida';
  }

  /** Cambia el tramo activo de un traslado. */
  setActiveLeg(index: number, leg: PreferenceLeg): void {
    if (this.cardStates[index]) {
      this.cardStates[index].activeLeg = leg;
    }
  }

  /** ¿El tramo ya tiene alguna preferencia cargada? */
  legHasPreferences(index: number, leg: PreferenceLeg): boolean {
    const group = this.preferenceGroup(index, leg);
    if (!group) return false;
    return ['modality', 'category', 'vehicles'].some(
      key => ((group.get(key)?.value || []) as any[]).length > 0
    );
  }

  isModalitySelected(index: number, leg: PreferenceLeg, value: string): boolean {
    return ((this.preferenceGroup(index, leg)?.get('modality')?.value || []) as string[]).includes(value);
  }

  isCategorySelected(index: number, leg: PreferenceLeg, value: string): boolean {
    return ((this.preferenceGroup(index, leg)?.get('category')?.value || []) as string[]).includes(value);
  }

  setModality(index: number, leg: PreferenceLeg, value: string): void {
    this.markReturnLegTouched(index, leg);
    this.preferenceGroup(index, leg)?.patchValue({ modality: [value] });
  }

  setCategory(index: number, leg: PreferenceLeg, value: string): void {
    this.markReturnLegTouched(index, leg);
    this.preferenceGroup(index, leg)?.patchValue({ category: [value] });
  }

  toggleVehicle(index: number, leg: PreferenceLeg, value: string): void {
    const group = this.preferenceGroup(index, leg);
    if (!group) return;
    this.markReturnLegTouched(index, leg);
    const current = (group.get('vehicles')?.value || []) as string[];
    const next = current.includes(value) ? current.filter(v => v !== value) : [...current, value];
    group.patchValue({ vehicles: next });
  }

  /** Marca que el usuario editó la vuelta a mano: deja de seguir a la ida. */
  private markReturnLegTouched(index: number, leg: PreferenceLeg): void {
    if (leg === 'vuelta' && this.cardStates[index]) {
      this.cardStates[index].returnLegTouched = true;
    }
  }

  isVehicleSelected(index: number, leg: PreferenceLeg, value: string): boolean {
    return ((this.preferenceGroup(index, leg)?.get('vehicles')?.value || []) as string[]).includes(value);
  }

  /**
   * Copia las preferencias de la ida a la vuelta. Sólo si el usuario no
   * editó la vuelta a mano (en ese caso gana lo que eligió explícitamente).
   */
  private syncIdaPreferencesToReturn(index: number): void {
    const state = this.cardStates[index];
    if (!state || state.returnLegTouched) return;
    const ida = this.preferenceGroup(index, 'ida');
    const vuelta = this.preferenceGroup(index, 'vuelta');
    if (!ida || !vuelta) return;
    vuelta.patchValue({
      modality: (ida.get('modality')?.value || []) as string[],
      category: (ida.get('category')?.value || []) as string[],
      vehicles: (ida.get('vehicles')?.value || []) as string[]
    }, { emitEvent: false });
  }

  // ==========================================================
  // Calendario
  // ==========================================================

  /** Título del mes visible en el calendario ("Octubre de 2026"). */
  get calendarTitle(): string {
    const month = this.calendarCursor.getMonth();
    return `${MONTH_LABELS[month]} de ${this.calendarCursor.getFullYear()}`;
  }

  /**
   * Celdas del mes visible: la primera fila arranca en lunes y las posiciones
   * que no pertenecen al mes quedan vacías.
   */
  get calendarDays(): (Date | null)[] {
    const year = this.calendarCursor.getFullYear();
    const month = this.calendarCursor.getMonth();
    const offset = (new Date(year, month, 1).getDay() + 6) % 7;
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const cells: (Date | null)[] = new Array(offset).fill(null);
    for (let day = 1; day <= daysInMonth; day++) {
      cells.push(new Date(year, month, day, 12));
    }
    while (cells.length % 7 !== 0) {
      cells.push(null);
    }
    return cells;
  }

  /** Fecha de recogida formateada para el input (DD/MM/YYYY). */
  pickupDateLabel(index: number): string {
    return this.formatDateLabel(this.transferGroup(index)?.get('pickupDate')?.value);
  }

  /** Fecha de vuelta formateada para el input (DD/MM/YYYY). */
  returnDateLabel(index: number): string {
    return this.formatDateLabel(this.transferGroup(index)?.get('returnDate')?.value);
  }

  /** Abre o cierra el calendario del campo indicado de un traslado. */
  toggleCalendar(index: number, field: TransferDateField): void {
    if (this.openCalendar?.index === index && this.openCalendar?.field === field) {
      this.closeCalendar();
      return;
    }
    const selected = this.toDate(this.transferGroup(index)?.get(field)?.value);
    this.calendarCursor = selected
      ? new Date(selected.getFullYear(), selected.getMonth(), 1)
      : new Date(new Date().getFullYear(), new Date().getMonth(), 1);
    this.openCalendar = { index, field };
  }

  /** ¿El calendario abierto es el de este traslado/campo? */
  isCalendarOpen(index: number, field: TransferDateField): boolean {
    return this.openCalendar?.index === index && this.openCalendar?.field === field;
  }

  closeCalendar(): void {
    this.openCalendar = null;
  }

  previousMonth(): void {
    this.calendarCursor = new Date(this.calendarCursor.getFullYear(), this.calendarCursor.getMonth() - 1, 1);
  }

  nextMonth(): void {
    this.calendarCursor = new Date(this.calendarCursor.getFullYear(), this.calendarCursor.getMonth() + 1, 1);
  }

  /** El día seleccionado se pinta como el del React (círculo negro). */
  isSelectedDate(index: number, field: TransferDateField, day: Date): boolean {
    const selected = this.toDate(this.transferGroup(index)?.get(field)?.value);
    if (!selected) return false;
    return selected.getFullYear() === day.getFullYear()
      && selected.getMonth() === day.getMonth()
      && selected.getDate() === day.getDate();
  }

  /** La fecha de vuelta no puede ser anterior a la de recogida. */
  isDateDisabled(index: number, field: TransferDateField, day: Date): boolean {
    const min = field === 'returnDate'
      ? this.toDate(this.transferGroup(index)?.get('pickupDate')?.value)
      : this.today();
    return min ? day.getTime() < min.getTime() : false;
  }

  /** Guarda la fecha elegida en formato ISO (YYYY-MM-DD). */
  selectDate(index: number, field: TransferDateField, day: Date): void {
    const control = this.transferGroup(index)?.get(field);
    if (!control) return;
    control.setValue(this.toIsoDate(day));
    control.markAsTouched();
    this.closeCalendar();
  }

  clearDate(index: number, field: TransferDateField): void {
    const control = this.transferGroup(index)?.get(field);
    if (!control) return;
    control.setValue('');
    control.markAsTouched();
    this.closeCalendar();
  }

  getReturnDateError(index: number): string {
    const control = this.transferGroup(index)?.get('returnDate');
    if (!control?.touched) return '';
    const group = this.transferGroup(index);
    if (group?.hasError('returnDateRequired')) return 'Seleccioná la fecha de vuelta.';
    if (group?.hasError('dateRange')) return 'La vuelta no puede ser anterior a la recogida.';
    return '';
  }

  getReturnTimeError(index: number): string {
    const control = this.transferGroup(index)?.get('returnTime');
    if (!control?.touched) return '';
    const group = this.transferGroup(index);
    if (group?.hasError('returnTimeRequired')) return 'Seleccioná la hora de vuelta.';
    if (group?.hasError('dateTimeRange')) return 'La hora de vuelta debe ser posterior a la de recogida.';
    return '';
  }

  // ==========================================================
  // Competencia / disparidad tarifaria
  // ==========================================================

  addCompetitorUrl(): void {
    this.competitorUrlsArray.push(this.fb.control('', [this.transferUrlValidator]));
  }

  removeCompetitorUrl(index: number): void {
    if (index < 0 || index >= this.competitorUrlsArray.length) return;
    this.competitorUrlsArray.removeAt(index);
  }

  /** Adjunta las capturas elegidas (se guardan en base64 dentro del producto). */
  onCompetitorImagesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input || !input.files) return;

    const files = Array.from(input.files)
      .filter(file => ['image/jpeg', 'image/png', 'image/webp'].includes(file.type));

    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = () => {
        this.competitorImagesArray.push(this.fb.control({ name: file.name, image: reader.result as string }));
      };
      reader.readAsDataURL(file);
    });

    // Limpia el input para poder volver a elegir el mismo archivo.
    input.value = '';
  }

  removeCompetitorImage(index: number): void {
    if (index < 0 || index >= this.competitorImagesArray.length) return;
    this.competitorImagesArray.removeAt(index);
  }

  /** true si algún enlace cargado no es una URL válida. */
  hasInvalidCompetitorUrl(): boolean {
    return this.competitorUrlsArray.controls.some(control => {
      const value = (control.value || '').trim();
      return value !== '' && !this.isValidUrl(value);
    });
  }

  private isValidUrl(value: string): boolean {
    try {
      const url = new URL(value);
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch {
      return false;
    }
  }

  /** Validator de los enlaces de referencia: vacío o http(s) válida. */
  private transferUrlValidator = (control: AbstractControl): ValidationErrors | null => {
    const value = (control.value || '').trim();
    if (!value) return null;
    return this.isValidUrl(value) ? null : { invalidUrl: true };
  };

  private applyUrlValidator(array: FormArray): void {
    array.controls.forEach(control => {
      (control as FormControl).setValidators(this.transferUrlValidator);
      control.updateValueAndValidity();
    });
  }

  // ==========================================================
  // Envío / limpieza
  // ==========================================================

  confirmClear(): void {
    this.showClearDialog = true;
    this.clearDialogChange.emit(true);
  }

  cancelClear(): void {
    this.showClearDialog = false;
    this.clearDialogChange.emit(false);
  }

  confirmClearAction(): void {
    this.formGroup.reset({
      requestType: 'quotation',
      file_id: '', fileName: '',
      client: { client_id: '', first_name: '', last_name: '', email: '', phone_code: '', phone: '' },
      passenger: { id: '', first_name: '', last_name: '' },
      transfers: [],
      manualFlights: [],
      comments: ''
    });

    // Los FormArray del bloque de competencia se limpian aparte: el reset del
    // grupo deja los controles con null en vez de vaciar el array.
    this.competitorUrlsArray.clear();
    this.competitorUrlsArray.push(this.fb.control(''));
    this.competitorImagesArray.clear();

    // Los traslados se vacían del todo (estado inicial del Lovable: la
    // pantalla muestra "Aún no agregaste ningún traslado").
    const transfers = this.formGroup.get('transfers') as FormArray;
    transfers.clear();
    this.syncCardStates();

    // Los vuelos cargados a mano también se borran.
    const manualFlights = this.formGroup.get('manualFlights') as FormArray;
    if (manualFlights) {
      manualFlights.clear();
    }
    this.manualFlightOpen = false;
    this.manualFlightDraft = this.emptyManualFlight();

    this.closeCalendar();
    this.showClearDialog = false;
    this.clearDialogChange.emit(false);
    this.clear.emit();
  }

  onSubmit(): void {
    if (this.formGroup.invalid || !this.requestReady) return;

    // El payload lo arma el componente padre (`buildPayloadTransfer`) para
    // que el envío del tab y el envío global sean idénticos.
    this.submit.emit({});
  }

  // ==========================================================
  // Utilidades de fecha
  // ==========================================================

  private today(): Date {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12);
  }

  /** Convierte el valor del control en Date (soporta ISO, Date o vacío). */
  private toDate(value: any): Date | null {
    if (!value) return null;
    if (value instanceof Date) {
      return isNaN(value.getTime()) ? null : value;
    }
    if (typeof value === 'string') {
      const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
      if (iso) {
        return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]), 12);
      }
      const parsed = new Date(value);
      return isNaN(parsed.getTime()) ? null : parsed;
    }
    return null;
  }

  /** Formatea la fecha del control como DD/MM/YYYY. */
  private formatDateLabel(value: any): string {
    const date = this.toDate(value);
    if (!date) return '';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${day}/${month}/${date.getFullYear()}`;
  }

  /** Formatea la fecha como YYYY-MM-DD (ISO). */
  private toIsoDate(date: Date): string {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
  }
}
